// PWA auto-update (web). Ported from the reference games' native/
// checkAppUpdate.js: compare the bundled BUILD_ID with the deployed
// version.json, and on mismatch clear caches + reload to the new build with a
// session loop-guard. On localhost / dev it is a no-op.

import { BUILD_ID, isDevMode } from "../config.ts";
import { key, evt } from "../../gameId.ts";

const UPDATE_GUARD_KEY = key("updateGuard");
export const UPDATE_EVENT = evt("update-required");
const FETCH_TIMEOUT_MS = 8000;
const MAX_RELOAD_ATTEMPTS = 2;

export interface VersionResult {
  action: "ok" | "reloading" | "manual";
  localId?: string;
  remoteId?: string;
}

function readGuard(): { remoteId?: string; attempts?: number } {
  try {
    return JSON.parse(sessionStorage.getItem(UPDATE_GUARD_KEY) || "{}");
  } catch {
    return {};
  }
}
function writeGuard(remoteId: string, attempts: number): void {
  try {
    sessionStorage.setItem(UPDATE_GUARD_KEY, JSON.stringify({ remoteId, attempts }));
  } catch {
    /* private mode */
  }
}

async function clearAllCaches(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
  } catch {
    /* best-effort */
  }
}

async function fetchRemoteBuildId(): Promise<string | null> {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(`./version.json?t=${Date.now()}`, {
      cache: "no-store",
      signal: ctrl.signal,
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data?.buildId ?? null;
  } catch {
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

function shouldReloadFor(remoteId: string): boolean {
  const guard = readGuard();
  if (guard.remoteId === remoteId && (guard.attempts ?? 0) >= MAX_RELOAD_ATTEMPTS) {
    return false;
  }
  const attempts = guard.remoteId === remoteId ? (guard.attempts ?? 0) + 1 : 1;
  writeGuard(remoteId, attempts);
  return true;
}

function reloadTo(remoteId: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set("v", remoteId);
  window.location.replace(url.toString());
}

export async function ensureAppVersion(): Promise<VersionResult> {
  if (isDevMode()) return { action: "ok" };
  if (/localhost|127\.0\.0\.1/i.test(window.location.origin)) return { action: "ok" };

  const remoteId = await fetchRemoteBuildId();
  if (!remoteId || remoteId === BUILD_ID) return { action: "ok" };
  if (!shouldReloadFor(remoteId)) {
    return { action: "manual", localId: BUILD_ID, remoteId };
  }
  await clearAllCaches();
  reloadTo(remoteId);
  return { action: "reloading" };
}

export async function retryManualUpdate(remoteId: string): Promise<void> {
  writeGuard(remoteId, 0);
  await clearAllCaches();
  reloadTo(remoteId);
}

export async function checkAppUpdateOnResume(): Promise<boolean> {
  const result = await ensureAppVersion();
  if (result.action === "manual") {
    window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: result }));
  }
  return result.action === "reloading";
}
